import { createApi } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { setToken, removeToken } from "../../utils/auth";
import {
  AdminDetail,
  AdminListItem,
  AdminLoginResponse,
  AdminProfile,
  ApiResponse,
  CreateAdminRequest,
  LoginCredentials,
} from "../../types";
import { toast } from "../../core/toast";

// ---------- API ----------
export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: baseQueryWithAuth, // Use shared base query
  tagTypes: ["Auth", "Profile", "Admins"],

  endpoints: (builder) => ({
    // ---------- LOGIN ----------
    login: builder.mutation<ApiResponse<AdminLoginResponse>, LoginCredentials>({
      query: (credentials) => ({
        url: ApiEndpoints.adminLogin,
        method: "PATCH",
        body: credentials,
      }),
      invalidatesTags: ["Auth", "Profile"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const token = data?.result?.token;
          if (token) setToken(token); // Store in cookie
          toast.success(data?.message || "Success");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    // ---------- PASSWORD OPS ----------
    forgotPassword: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { email: string }
    >({
      query: (body) => ({
        url: `${ApiEndpoints.adminForgetPassword}`,
        method: "PATCH",
        body,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Success");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    resetPassword: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { currentPassword: string; newPassword: string }
    >({
      query: (body) => ({
        url: `${ApiEndpoints.adminResetPassword}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Profile"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Success");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    // ---------- PROFILE ----------
    getProfile: builder.query<ApiResponse<AdminProfile>, void>({
      query: () => `${ApiEndpoints.adminProfile}`,
      providesTags: ["Profile"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // toast.success(data?.message || "Success");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    // ---------- CREATE ADMIN ----------
    createAdmin: builder.mutation<
      ApiResponse<AdminProfile>,
      CreateAdminRequest
    >({
      query: (body) => ({
        url: `${ApiEndpoints.admin}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Admins"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Success");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    // ---------- LOGOUT ----------
    logout: builder.mutation<ApiResponse<{ ok: boolean }>, void>({
      query: () => ({
        url: `${ApiEndpoints.adminLogout}`,
        method: "PATCH",
      }),
      invalidatesTags: ["Auth", "Profile"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Success");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        } finally {
          removeToken(); // Remove from cookie
        }
      },
    }),

    // ---------- GET ADMINS ----------
    getAdmins: builder.query<AdminListItem[], void>({
      query: () => ({
        url: `${ApiEndpoints.admin}`,
        method: "GET",
      }),
      transformResponse: (res: any) => {
        const list = Array.isArray(res?.results) ? res.results : [];
        const normStatus = (s: any) =>
          String(s ?? "Active").toLowerCase() === "inactive"
            ? "Inactive"
            : "Active";
        const orgFromAssigned = (arr: any[]) => {
          if (!Array.isArray(arr) || arr.length === 0) return "";
          const first = arr[0];
          return first?.name ?? first?.title ?? "";
        };
        return list.map((a: any) => ({
          id: String(a?._id ?? a?.id ?? ""),
          name: a?.name ?? "",
          role: a?.role ?? a?.designation ?? "",
          email: a?.email ?? "",
          phone: String(a?.phone ?? a?.mobile ?? ""),
          org:
            a?.organisation ??
            a?.org ??
            orgFromAssigned(a?.assignedOrganisations) ?? // fixed: pass array, not .name
            "",
          status: normStatus(a?.status),
        }));
      },
      providesTags: ["Admins"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    // ---------- DELETE ADMIN ----------
    deleteAdmin: builder.mutation<ApiResponse<{ ok: boolean }>, string>({
      query: (id) => ({
        url: `${ApiEndpoints.admin}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Admins"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Success");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    // ---------- GET ADMIN BY ID ----------
    getAdminById: builder.query<AdminDetail, string>({
      query: (id) => ({
        url: `${ApiEndpoints.admin}/${id}`,
        method: "GET",
      }),
      transformResponse: (res: any): AdminDetail => {
        // Return the server response (prefer `result` when present)
        return (res?.result ?? res) as AdminDetail;
      },
      providesTags: ["Admins"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Success");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    // ---------- EDIT ADMIN ----------
    editAdminById: builder.mutation<
      ApiResponse<AdminProfile>,
      { id: string; data: Partial<AdminProfile> }
    >({
      query: ({ id, data }) => ({
        url: `${ApiEndpoints.admin}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Admins", "Profile"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || "Success");
        } catch (err: any) {
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),

    // ---------- TOGGLE STATUS ----------
    toggleAdminStatus: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { id: string; status: "Active" | "Inactive" }
    >({
      query: ({ id, status }) => ({
        url: `${ApiEndpoints.adminStatusChange}/${id}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Admins"],
      async onQueryStarted({ id, status }, { dispatch, queryFulfilled }) {
        // Optimistic update in list
        const patchList = dispatch(
          adminApi.util.updateQueryData(
            "getAdmins",
            undefined,
            (draft: Draft<AdminListItem[]>) => {
              const row = draft.find((r) => r.id === id);
              if (row) row.status = status;
            }
          )
        );

        // Optimistic update in detail (if cached)
        const patchDetail = dispatch(
          adminApi.util.updateQueryData(
            "getAdminById",
            id,
            (draft: Draft<AdminDetail>) => {
              if (draft) draft.status = status;
            }
          )
        );

        try {
          await queryFulfilled; // server confirms toggle
          toast.success("Status updated successfully");
        } catch (err: any) {
          // rollback on error
          patchList.undo();
          patchDetail.undo();
          toast.error(err?.error?.data?.message || "Error");
        }
      },
    }),
  }),
});

// ---------- EXPORT HOOKS ----------
export const {
  useLoginMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useGetProfileQuery,
  useCreateAdminMutation,
  useLogoutMutation,
  useGetAdminsQuery,
  useDeleteAdminMutation,
  useGetAdminByIdQuery,
  useEditAdminByIdMutation,
  useToggleAdminStatusMutation,
} = adminApi;
